'use client';

import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Command as CommandDefinition } from './types';
import { getAiCommandCandidates } from './command-ai';
import { COMMAND_PALETTE_AI_MODEL, COMMAND_PALETTE_AI_PROVIDER, COMMAND_PALETTE_INTERPRET_ENDPOINT, commandPaletteInterpretationSchema } from './command-palette-ai-contract';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
export type CommandPaletteAiState = {
  status: 'idle';
} | {
  status: 'loading';
  query: string;
  candidateCount: number;
} | {
  status: 'suggest' | 'clarify' | 'none' | 'error';
  query: string;
  alternatives: string[];
  question?: string | null;
  message?: string;
  confidence?: number;
};
type UseCommandPaletteAiOptions = {
  candidateCommands: CommandDefinition[];
  isPrSearchActive: boolean;
  onExecuteCommand: (commandId: string) => void;
  pathname: string;
  routeSearchParams: ReadonlyURLSearchParams | undefined;
  searchTerm: string;
  visibleCommands: CommandDefinition[];
};
function getSearchParamsRecord(searchParams: ReadonlyURLSearchParams | undefined) {
  const searchParamsRecord: Record<string, string> = {};
  searchParams?.forEach((value, key) => {
    searchParamsRecord[key] = value;
  });
  return searchParamsRecord;
}
function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}
function getLoadingCommandLabel(candidateCount: number) {
  return `${candidateCount} command${candidateCount === 1 ? '' : 's'}`;
}
export function useCommandPaletteAi({
  candidateCommands,
  isPrSearchActive,
  onExecuteCommand,
  pathname,
  routeSearchParams,
  searchTerm,
  visibleCommands
}: UseCommandPaletteAiOptions) {
  const [state, setState] = useState<CommandPaletteAiState>({
    status: 'idle'
  });
  const [showLoadingDetail, setShowLoadingDetail] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const searchParamsKey = routeSearchParams?.toString() ?? '';
  const canAskAi = !isPrSearchActive && searchTerm.trim().length > 0 && state.status !== 'loading';
  const alternativeCommands = useMemo(() => {
    if (state.status !== 'suggest' && state.status !== 'clarify' && state.status !== 'error') {
      return [];
    }
    return state.alternatives.map(commandId => visibleCommands.find(command => command.id === commandId)).filter((command_0): command_0 is CommandDefinition => Boolean(command_0));
  }, [state, visibleCommands]);
  const loadingLabel = showLoadingDetail && state.status === 'loading' ? `Checking ${getLoadingCommandLabel(state.candidateCount)}...` : 'Searching commands...';
  const reset = useCallback(() => {
    requestIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setShowLoadingDetail(false);
    setState(previousState => previousState.status === 'idle' ? previousState : {
      status: 'idle'
    });
  }, []);
  useEffect(() => {
    requestIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, [isPrSearchActive, pathname, searchParamsKey, searchTerm]);
  useEffect(() => {
    if (state.status !== 'loading') {
      return;
    }
    const timeout = window.setTimeout(() => {
      setShowLoadingDetail(true);
    }, 800);
    return () => window.clearTimeout(timeout);
  }, [state.status]);
  const ask = useCallback(async () => {
    const query = searchTerm.trim();
    if (!query || isPrSearchActive || state.status === 'loading') {
      return;
    }
    const candidates = getAiCommandCandidates(candidateCommands, query);
    if (candidates.length === 0) {
      setState({
        status: 'none',
        query,
        alternatives: [],
        message: 'No AI-routable commands are available here.'
      });
      trackClientEvent('command_palette_ai_rejected', undefined, {
        query_length: query.length,
        candidate_count: 0,
        route: pathname,
        reason: 'no_candidates',
        model: COMMAND_PALETTE_AI_MODEL,
        provider: COMMAND_PALETTE_AI_PROVIDER
      });
      return;
    }
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isActiveRequest = () => requestIdRef.current === requestId && !abortController.signal.aborted;
    const startedAt = performance.now();
    setShowLoadingDetail(false);
    setState({
      status: 'loading',
      query,
      candidateCount: candidates.length
    });
    trackClientEvent('command_palette_ai_requested', undefined, {
      query_length: query.length,
      candidate_count: candidates.length,
      route: pathname,
      model: COMMAND_PALETTE_AI_MODEL,
      provider: COMMAND_PALETTE_AI_PROVIDER
    });
    try {
      const response = await fetch(COMMAND_PALETTE_INTERPRET_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: abortController.signal,
        body: JSON.stringify({
          query,
          currentPath: pathname,
          currentSearchParams: getSearchParamsRecord(routeSearchParams),
          candidates
        })
      });
      if (!response.ok) {
        throw new Error(`Command palette AI request failed with ${response.status}`);
      }
      const parsedInterpretation = commandPaletteInterpretationSchema.safeParse(await response.json());
      if (!parsedInterpretation.success) {
        throw new Error('Command palette AI response failed validation');
      }
      if (!isActiveRequest()) {
        return;
      }
      const interpretation = parsedInterpretation.data;
      const latencyMs = Math.round(performance.now() - startedAt);
      trackClientEvent('command_palette_ai_result_shown', undefined, {
        query_length: query.length,
        candidate_count: candidates.length,
        route: pathname,
        result_type: interpretation.type,
        command_id: interpretation.commandId,
        alternative_count: interpretation.alternatives.length,
        confidence: interpretation.confidence,
        latency_ms: latencyMs,
        model: COMMAND_PALETTE_AI_MODEL,
        provider: COMMAND_PALETTE_AI_PROVIDER
      });
      if (interpretation.type === 'execute' && interpretation.commandId) {
        const command_1 = visibleCommands.find(candidate => candidate.id === interpretation.commandId);
        if (!command_1 || command_1.safeExecution !== 'navigation' || command_1.tag) {
          setState({
            status: 'error',
            query,
            alternatives: interpretation.alternatives,
            message: 'That command cannot be run from here.',
            confidence: interpretation.confidence
          });
          trackClientEvent('command_palette_ai_rejected', undefined, {
            query_length: query.length,
            candidate_count: candidates.length,
            route: pathname,
            command_id: interpretation.commandId,
            reason: command_1 ? 'unsafe_command' : 'unknown_command',
            confidence: interpretation.confidence,
            model: COMMAND_PALETTE_AI_MODEL,
            provider: COMMAND_PALETTE_AI_PROVIDER
          });
          return;
        }
        trackClientEvent('command_palette_ai_executed', undefined, {
          query_length: query.length,
          candidate_count: candidates.length,
          route: pathname,
          command_id: interpretation.commandId,
          confidence: interpretation.confidence,
          latency_ms: latencyMs,
          model: COMMAND_PALETTE_AI_MODEL,
          provider: COMMAND_PALETTE_AI_PROVIDER
        });
        onExecuteCommand(interpretation.commandId);
        return;
      }
      setState({
        status: interpretation.type === 'execute' ? 'suggest' : interpretation.type,
        query,
        alternatives: interpretation.alternatives,
        question: interpretation.question,
        confidence: interpretation.confidence
      });
    } catch (error) {
      if (!isActiveRequest() || isAbortError(error)) {
        return;
      }
      console.warn('Command palette AI request failed', error);
      setState({
        status: 'error',
        query,
        alternatives: [],
        message: 'AI search is unavailable right now.'
      });
      trackClientEvent('command_palette_ai_rejected', undefined, {
        query_length: query.length,
        candidate_count: candidates.length,
        route: pathname,
        reason: 'request_failed',
        model: COMMAND_PALETTE_AI_MODEL,
        provider: COMMAND_PALETTE_AI_PROVIDER
      });
    }
  }, [candidateCommands, isPrSearchActive, onExecuteCommand, pathname, routeSearchParams, searchTerm, state.status, visibleCommands]);
  return {
    alternativeCommands,
    ask,
    canAskAi,
    loadingLabel,
    reset,
    state
  };
}